import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Flex, PromptBody, PromptButton, PromptHeader } from "../styles/global";
import { getQuizzes, launchQuiz, deleteQuiz } from "../utils/api.utils";
import { QuizEditor } from "../pages/QuizEditor";
import { QuizDisplay } from "../pages/QuizDisplay";
import { QuizData } from "../models";
import { useIsComponentMounted } from '../utils/hooks.utils'
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import sortBy from "lodash/sortBy";
import moment from "moment";
import quizIcon from "../assets/quiz.svg";

interface Props {
  code: string;
  initialItems?: any[];
  onQuizzesUpdate?: (quizzes: any[]) => void;
}

export const QuizList = ({ code, initialItems, onQuizzesUpdate }: Props) => {
  const [showEditScreen, setEditScreen] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState<QuizData>();
  const [currentQuizIndex, setCurrentQuizIndex] = useState<number>(-1);
  const [quizzes, setQuizzes] = useState<QuizData[]>(initialItems ?? []);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isComponentMounted = useIsComponentMounted();

  const fetchQuizzes = useCallback(() => {
    // If seeded with initial data (clone scenario), don't fetch from backend
    if (initialItems) return;
    getQuizzes(code)
      .then((result) => {
        if (isComponentMounted.current) {
          const payload = result as any;
          setQuizzes(sortBy(payload, "created_at"));
        }
      })
      .catch((err) => {
        if (isComponentMounted.current) {
          setQuizzes([])
        }
      });
  }, [code, initialItems, isComponentMounted]);

  // Handler for updating quizzes in memory (for unsaved simulations)
  const updateQuizInMemory = useCallback((updatedQuiz: any) => {
    setQuizzes(prevQuizzes => {
      let newQuizzes;
      if (currentQuizIndex >= 0 && currentQuizIndex < prevQuizzes.length) {
        // Update existing quiz at the tracked index
        newQuizzes = [...prevQuizzes];
        newQuizzes[currentQuizIndex] = updatedQuiz;
      } else {
        // Add new quiz
        newQuizzes = [...prevQuizzes, updatedQuiz];
      }
      // Notify parent of the update
      if (onQuizzesUpdate) {
        onQuizzesUpdate(newQuizzes);
      }
      return newQuizzes;
    });
    setEditScreen(false);
    setCurrentQuizIndex(-1);
  }, [currentQuizIndex, onQuizzesUpdate]);

  useEffect(() => {
    fetchQuizzes()
  }, [fetchQuizzes]);


  const createNewQuiz = () => {
    setCurrentQuiz({
      quiz_id: 0,
      title: "",
      description: "",
      locked: false,
      questions: [],
      code,
    });
    setCurrentQuizIndex(-1); // -1 indicates a new quiz
    setEditScreen(true);
  };

  const displayQuizModal = (quiz: any) => {
    if (!quiz) {
      return;
    }
    setCurrentQuiz(quiz);
    setShowLaunchModal(true);
  };

  const displayDeleteModal = (quiz: any) => {
    if (!quiz) {
      return;
    }
    setCurrentQuiz(quiz);
    setShowDeleteModal(true);
  };

  const handleQuizLaunch = (code: string, quiz: any) => {
    launchQuiz(code, quiz);
    setCurrentQuiz(undefined);
    setShowLaunchModal(false);
    fetchQuizzes();
  };

  const selectQuiz = (targetQuiz: any, index: number) => {
    setCurrentQuiz(targetQuiz);
    setCurrentQuizIndex(index);
    setEditScreen(true);
  };

  const handleQuizDelete = (targetQuiz: any) => {
    deleteQuiz(targetQuiz.quiz_id)
      .then((res) => console.log(res))
      .catch((err) => console.error(err));
    setCurrentQuiz(undefined);
    setShowDeleteModal(false);
    setTimeout(() => fetchQuizzes(), 1000);
    fetchQuizzes();
  };

  const displayQuizzes = () => {
    if (quizzes) {
      return (
        <div style={{ padding: '0 0 24px 0', width: '100%', boxSizing: 'border-box' }}>
          {showEditScreen ?
            currentQuiz && !currentQuiz.locked ?
              <QuizEditor
                shouldDisplay={setEditScreen}
                fetchQuizzes={fetchQuizzes}
                quiz={currentQuiz}
                onSave={initialItems ? updateQuizInMemory : undefined}
              />
              :
              <QuizDisplay shouldDisplay={setEditScreen} quiz={currentQuiz} />
            :
            <>
              <Flex justify="space-between" alignItems="center" style={{ marginBottom: '32px' }}>
                <h2 style={{ fontWeight: 800, margin: 0, fontSize: '28px' }}>Quizzes for <span style={{ color: '#3f51b5' }}>{code}</span></h2>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ borderRadius: '8px', padding: '10px 24px', fontWeight: 600, boxShadow: '0 4px 12px rgba(63, 81, 181, 0.3)' }}
                  onClick={() => createNewQuiz()}>
                  + New Quiz
                </Button>
              </Flex>
              <div id="quizzes-view">
                {quizzes &&
                  quizzes.map((item: any, index: number) => (
                    <QuizCard
                      locked={item.locked}
                      key={`${item.quiz_id || item.id}-${item.title}`}
                      direction="row"
                      justify="space-between"
                      alignItems="center"
                    >
                      <QuizInfo direction="row" alignItems="center" style={{ flex: 1 }}>
                        <IconContainer locked={item.locked}>
                          <img src={quizIcon} alt="Quiz" />
                        </IconContainer>
                        <QuizTextContent direction="column">
                          <Flex alignItems="center" style={{ marginBottom: '4px' }}>
                            <QuizTitle>{item.title || "Untitled Quiz"}</QuizTitle>
                            {item.locked ? (
                              <StatusBadge status="live">LIVE</StatusBadge>
                            ) : (
                              <StatusBadge status="draft">DRAFT</StatusBadge>
                            )}
                          </Flex>
                          {item.description && <DescriptionText>{item.description}</DescriptionText>}
                          <Flex wrap="wrap" alignItems="center">
                            <StatPill>ID: {item.quiz_id}</StatPill>
                            <StatPill>{item.questions?.length || 0} Questions</StatPill>
                            <TimestampText>
                              Updated {moment(item.created_at).fromNow()}
                            </TimestampText>
                          </Flex>
                        </QuizTextContent>
                      </QuizInfo>

                      <ActionGroup alignItems="center">
                        {!item.locked ? (
                          <>
                            <ActionButton
                              variant="outlined"
                              onClick={() => selectQuiz(item, index)}
                            >
                              Edit
                            </ActionButton>
                            <ActionButton
                              $primary
                              onClick={(e) => {
                                (e.currentTarget as HTMLElement).blur();
                                displayQuizModal(item);
                              }}
                            >
                              Launch
                            </ActionButton>
                          </>
                        ) : (
                          <ActionButton
                            variant="outlined"
                            onClick={() => selectQuiz(item, index)}
                          >
                            View
                          </ActionButton>
                        )}
                        <DeleteButton onClick={(e) => {
                          (e.currentTarget as HTMLElement).blur();
                          displayDeleteModal(item);
                        }}>
                          Delete
                        </DeleteButton>
                      </ActionGroup>
                    </QuizCard>
                  ))}
                <Dialog
                  open={showLaunchModal}
                  onClose={() => setShowLaunchModal(false)}
                  PaperProps={{
                    style: { borderRadius: '16px', padding: '8px', maxWidth: '450px' }
                  }}
                >
                  <ModalContent direction="column" alignItems="center">
                    <div className="icon-container launch">✓</div>
                    <PromptHeader style={{ color: '#1a202c', marginBottom: '12px', fontSize: '24px', fontWeight: 800 }}>
                      Launch Quiz?
                    </PromptHeader>
                    <PromptBody style={{ color: '#4a5568', textAlign: 'center', lineHeight: 1.6, fontSize: '15px' }}>
                      This will make the quiz public and <strong>lock it from further edits</strong>. This action cannot be undone.
                    </PromptBody>
                    <Flex style={{ marginTop: '32px', width: '100%', gap: '12px' }} justify="center">
                      <PromptButton
                        autoFocus
                        onClick={() => setShowLaunchModal(false)}
                        variant="outlined"
                        style={{
                          borderRadius: '8px',
                          padding: '10px 24px',
                          fontWeight: 600,
                          textTransform: 'none',
                          flex: 1,
                          borderColor: '#e2e8f0'
                        }}
                      >
                        Cancel
                      </PromptButton>
                      <PromptButton
                        onClick={() => handleQuizLaunch(code, currentQuiz)}
                        variant="contained"
                        style={{
                          background: '#008500',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '10px 24px',
                          fontWeight: 600,
                          textTransform: 'none',
                          flex: 1,
                          boxShadow: '0 4px 12px rgba(0, 133, 0, 0.2)'
                        }}
                      >
                        Yes, Launch
                      </PromptButton>
                    </Flex>
                  </ModalContent>
                </Dialog>

                <Dialog
                  open={showDeleteModal}
                  onClose={() => setShowDeleteModal(false)}
                  PaperProps={{
                    style: { borderRadius: '16px', padding: '8px', maxWidth: '450px' }
                  }}
                >
                  <ModalContent direction="column" alignItems="center">
                    <div className="icon-container delete">!</div>
                    <PromptHeader style={{ color: '#1a202c', marginBottom: '12px', fontSize: '24px', fontWeight: 800 }}>
                      Delete Quiz?
                    </PromptHeader>
                    <PromptBody style={{ color: '#4a5568', textAlign: 'center', lineHeight: 1.6, fontSize: '15px' }}>
                      Are you sure you want to delete this quiz? All data associated with it will be permanently removed.
                    </PromptBody>
                    <Flex style={{ marginTop: '32px', width: '100%', gap: '12px' }} justify="center">
                      <PromptButton
                        autoFocus
                        onClick={() => setShowDeleteModal(false)}
                        variant="outlined"
                        style={{
                          borderRadius: '8px',
                          padding: '10px 24px',
                          fontWeight: 600,
                          textTransform: 'none',
                          flex: 1,
                          borderColor: '#e2e8f0'
                        }}
                      >
                        Cancel
                      </PromptButton>
                      <PromptButton
                        onClick={() => handleQuizDelete(currentQuiz)}
                        variant="contained"
                        style={{
                          background: '#c10000',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '10px 24px',
                          fontWeight: 600,
                          textTransform: 'none',
                          flex: 1,
                          boxShadow: '0 4px 12px rgba(193, 0, 0, 0.2)'
                        }}
                      >
                        Delete
                      </PromptButton>
                    </Flex>
                  </ModalContent>
                </Dialog>
              </div>
            </>
          }
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {displayQuizzes()}
    </div>
  );
};

const ModalContent = styled(Flex)`
  padding: 32px;
  
  .icon-container {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 20px;
    
    &.delete {
      background: #fff5f5;
      color: #e53e3e;
      border: 2px solid #feb2b2;
    }
    
    &.launch {
      background: #f0fff4;
      color: #38a169;
      border: 2px solid #c6f6d5;
    }
  }
`;

const QuizCard = styled(Flex) <{ locked: boolean }>`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(149, 157, 165, 0.15);
    border-color: #cbd5e0;
  }

  ${({ locked }) => locked && `
    background: #f8fafc;
  `}
`;

const IconContainer = styled.div<{ locked: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${props => props.locked ? '#edf2f7' : '#e0e7ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  flex-shrink: 0;

  img {
    width: 28px;
    height: 28px;
    opacity: 0.8;
  }
`;

const QuizInfo = styled(Flex)``;

const QuizTextContent = styled(Flex)`
  flex: 1;
`;

const QuizTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  margin-right: 12px;
`;

const DescriptionText = styled.p`
  color: #4a5568;
  font-size: 15px;
  margin: 4px 0 12px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StatusBadge = styled.span<{ status: 'live' | 'draft' }>`
  font-size: 11px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.05em;
  background: ${props => props.status === 'live' ? '#C6F6D5' : '#E2E8F0'};
  color: ${props => props.status === 'live' ? '#22543D' : '#4A5568'};
`;

const StatPill = styled.span`
  font-size: 13px;
  color: #718096;
  background: #f7fafc;
  padding: 2px 10px;
  border-radius: 20px;
  margin-right: 12px;
  border: 1px solid #edf2f7;
`;

const TimestampText = styled.span`
  font-size: 13px;
  color: #a0aec0;
`;

const ActionGroup = styled(Flex)`
  margin-left: 24px;
  gap: 12px;
`;

const ActionButton = styled(Button) <{ $primary?: boolean }>`
  && {
    min-width: 100px;
    height: 40px;
    text-transform: none;
    font-weight: 600;
    border-radius: 8px;
    
    ${props => props.$primary ? `
      background: #3f51b5;
      color: white;
      &:hover {
        background: #303f9f;
      }
    ` : `
      border-color: #e2e8f0;
      color: #4a5568;
      &:hover {
        background: #f7fafc;
        border-color: #cbd5e0;
      }
    `}
  }
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 80px;
    height: 40px;
    text-transform: none;
    font-weight: 600;
    color: #e53e3e;
    &:hover {
      background: #fff5f5;
    }
  }
`;
